import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import {
  Ban,
  CheckCircle,
  Edit,
  Key,
  Trash2,
  UserCog,
} from "lucide-react";

import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { EditPageLayout } from "@/components/shared/EditPageLayout";
import { FileUpload } from "@/components/shared/FileUpload";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDisplayDate } from "@/lib/dateFormat";
import { tenantMemberKeys } from "@/lib/queryKeys";
import { requestPasswordReset } from "@/services/authService";
import {
  listTenantMembers,
  removeMember,
  setMemberStatus,
  updateMemberProfile,
  updateMemberRole,
  uploadMemberAvatar,
  type TenantMember,
  type TenantInviteRole,
} from "@/services/tenantUsersService";

function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "disabled":
    case "expired":
      return "danger";
    default:
      return "info";
  }
}

function getMemberDisplayName(m: TenantMember): string {
  const parts = [m.firstName, m.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (m.displayName?.trim()) return m.displayName;
  return m.email;
}

export function UserDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantSwitcher();

  const tenantId = activeTenant?.id ?? null;

  /**
   * Router state is the fast path — the users list and the Estado fiscal cards
   * all hand the member over on navigate, so the page renders with no request.
   *
   * The fetch is the fallback, and it is what makes this route ADDRESSABLE. The
   * page previously read location.state only, so any arrival without it sat on
   * the loading spinner forever: a bookmarked or shared /users/:id link, a
   * refresh, or simply a call site that forgot the state (which is exactly how
   * this surfaced — Estado fiscal's "Ver perfil" navigated bare while the
   * sibling accountants card next to it passed state correctly).
   *
   * Reuses the existing members list rather than adding a by-id endpoint: it is
   * already cached under the same query key the users page populates, so in
   * practice this usually resolves from cache without a round trip.
   */
  const stateMember = (location.state as { member?: TenantMember } | null)
    ?.member;
  const needsFetch = !stateMember || stateMember.userId !== userId;
  const { data: fetchedMembers, isLoading: isLoadingMembers } = useQuery({
    queryKey: tenantMemberKeys.list(tenantId ?? ""),
    queryFn: () => listTenantMembers(tenantId!),
    enabled: needsFetch && Boolean(tenantId),
  });
  const member =
    stateMember && stateMember.userId === userId
      ? stateMember
      : fetchedMembers?.find((m) => m.userId === userId);

  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [changeRoleSelectedRole, setChangeRoleSelectedRole] =
    useState<TenantInviteRole>("read_only");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editRole, setEditRole] = useState<TenantInviteRole>("read_only");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    // Only bounce once we KNOW the member is not there — firing while the
    // fallback fetch is still running would redirect away from a page that was
    // about to render fine.
    if (!member && userId && !isLoadingMembers && fetchedMembers) {
      navigate("/users", { replace: true });
    }
  }, [member, userId, navigate, isLoadingMembers, fetchedMembers]);

  useEffect(() => {
    if (member && member.role !== "owner") {
      const role = member.role as TenantInviteRole;
      if (["admin", "accountant", "read_only"].includes(role)) {
        setChangeRoleSelectedRole(role);
      }
    }
  }, [changeRoleOpen, member?.role]);

  const updateRoleMutation = useMutation({
    mutationFn: ({
      userId: uid,
      role,
    }: {
      userId: string;
      role: TenantInviteRole;
    }) => updateMemberRole(tenantId!, uid, role),
    onSuccess: () => {
      setChangeRoleOpen(false);
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      toast.success(t("users.tenant.roleUpdated"));
    },
    onError: () => {
      toast.error(t("users.tenant.roleUpdateError"));
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: ({
      userId: uid,
      ...payload
    }: {
      userId: string;
      firstName: string | null;
      lastName: string | null;
    }) => updateMemberProfile(tenantId!, uid, payload),
    onSuccess: (_, variables) => {
      setIsEditMode(false);
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      if (member && userId) {
        navigate(`/users/${userId}`, {
          replace: true,
          state: {
            member: {
              ...member,
              firstName: variables.firstName ?? null,
              lastName: variables.lastName ?? null,
            },
          },
        });
      }
      toast.success(t("users.detail.profileUpdated"));
    },
    onError: () => {
      toast.error(t("users.detail.profileUpdateError"));
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onSuccess: () => {
      setResetPasswordOpen(false);
      toast.success(t("users.detail.resetPasswordSent"));
    },
    onError: () => {
      toast.error(t("users.detail.resetPasswordError"));
    },
  });

  const setStatusMutation = useMutation({
    mutationFn: ({
      userId: uid,
      status,
    }: {
      userId: string;
      status: "active" | "disabled";
    }) => setMemberStatus(tenantId!, uid, status),
    onSuccess: (_, variables) => {
      setDisableOpen(false);
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      if (member) {
        navigate(".", {
          replace: true,
          state: { member: { ...member, status: variables.status } },
        });
      }
      toast.success(
        variables.status === "active"
          ? t("users.detail.accountEnabled")
          : t("users.detail.accountDisabled")
      );
    },
    onError: () => {
      toast.error(t("users.detail.statusUpdateError"));
    },
  });

  const deleteMemberMutation = useMutation({
    mutationFn: ({ userId: uid }: { userId: string }) =>
      removeMember(tenantId!, uid),
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId!),
      });
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.list(tenantId!),
      });
      toast.success(t("users.detail.memberRemoved"));
      navigate("/users", { replace: true });
    },
    onError: () => {
      toast.error(t("users.detail.removeError"));
    },
  });

  if (!tenantId || !userId) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("users.detail.title")}
          onBack={() => navigate("/users")}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("users.detail.invalidContext")}
        </p>
      </div>
    );
  }

  if (!member || member.userId !== userId) {
    return <LoadingPage />;
  }

  const editableRoles: TenantInviteRole[] = [
    "owner",
    "admin",
    "accountant",
    "read_only",
  ];
  const isOwner = member.role === "owner";
  const displayName = getMemberDisplayName(member);

  const handleSave = async (): Promise<void> => {
    if (!tenantId || !userId) return;
    setIsSavingEdit(true);
    try {
      let newProfileImage: string | null = member.profileImage ?? null;
      if (selectedFile) {
        const res = await uploadMemberAvatar(
          tenantId,
          member.userId,
          selectedFile
        );
        newProfileImage = res.profileImage;
      }
      await updateMemberProfile(tenantId, member.userId, {
        firstName: editFirstName.trim() || null,
        lastName: editLastName.trim() || null,
      });
      if (!isOwner && editRole !== member.role) {
        await updateMemberRole(tenantId, member.userId, editRole);
      }
      queryClient.invalidateQueries({
        queryKey: tenantMemberKeys.all(tenantId),
      });
      navigate(`/users/${userId}`, {
        replace: true,
        state: {
          member: {
            ...member,
            firstName: editFirstName.trim() || null,
            lastName: editLastName.trim() || null,
            role: isOwner ? member.role : editRole,
            profileImage: newProfileImage,
          },
        },
      });
      setIsEditMode(false);
      toast.success(t("users.detail.profileUpdated"));
    } catch {
      toast.error(t("users.detail.profileUpdateError"));
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSave();
  };

  if (isEditMode) {
    return (
      <EditPageLayout
        title={displayName}
        description={t("users.detail.subtitle")}
        isLoading={false}
        formId="user-edit-form"
        onSubmit={handleEditSubmit}
        onCancel={() => setIsEditMode(false)}
        cancelLabel={t("users.invite.cancel")}
        onSave={() => void handleSave()}
        saveLabel={t("tenants.edit.save")}
        savingLabel={t("tenants.edit.saving")}
        isSaving={isSavingEdit}
        entityExists={true}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              {t("users.detail.edit.profileImageLabel")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("users.detail.edit.profileImageHelper")}
            </p>
            <FileUpload
              currentImageUrl={profileImageUrl}
              onFileChange={(file) => {
                setSelectedFile(file);
                if (!file) {
                  setProfileImageUrl(member.profileImage ?? null);
                }
              }}
              disabled={isSavingEdit}
              showLabel={false}
            />
          </div>
          <form
            id="user-edit-form"
            className="space-y-6"
            onSubmit={handleEditSubmit}
          >
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">
                {t("users.detail.sections.personalInfo")}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="user-edit-firstName">
                    {t("users.detail.fields.firstName")}
                  </Label>
                  <Input
                    id="user-edit-firstName"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    disabled={isSavingEdit}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="user-edit-lastName">
                    {t("users.detail.fields.lastName")}
                  </Label>
                  <Input
                    id="user-edit-lastName"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    disabled={isSavingEdit}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="user-edit-email">
                  {t("users.page.columns.email")}
                </Label>
                <Input
                  id="user-edit-email"
                  type="email"
                  value={member.email}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="user-edit-role">
                  {t("users.page.columns.role")}
                </Label>
                {isOwner ? (
                  <p className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
                    {t(`users.tenant.role.${member.role}`)}
                  </p>
                ) : (
                  <Select
                    value={editRole}
                    onValueChange={(v) => setEditRole(v as TenantInviteRole)}
                    disabled={isSavingEdit}
                  >
                    <SelectTrigger id="user-edit-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {editableRoles.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`users.tenant.role.${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </form>
        </div>
      </EditPageLayout>
    );
  }

  return (
    // fx-content is the portal's page container (max-width 1280, centred, own
    // padding, plus the collapsed-sidebar rule). 53 pages use it — including
    // users-page and the sibling invite-detail-page. This one was the only page
    // on a bare `p-6`, which is why it alone ran edge-to-edge.
    <div className="space-y-6 fx-content">
      <PageHeader
        title={displayName}
        description={t("users.detail.subtitle")}
        onBack={() => navigate("/users")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditFirstName(member.firstName ?? "");
                setEditLastName(member.lastName ?? "");
                setEditRole(
                  isOwner ? "read_only" : (member.role as TenantInviteRole)
                );
                setProfileImageUrl(member.profileImage ?? null);
                setSelectedFile(null);
                setIsEditMode(true);
              }}
              disabled={updateProfileMutation.isPending}
            >
              <Edit className="mr-2 h-4 w-4" />
              {t("users.detail.actions.edit")}
            </Button>
            {!isOwner && (
              <Button
                variant="outline"
                onClick={() => setChangeRoleOpen(true)}
                disabled={updateRoleMutation.isPending}
              >
                <UserCog className="mr-2 h-4 w-4" />
                {t("users.tenant.changeRole")}
              </Button>
            )}
          </div>
        }
      />

      <Card className="overflow-hidden">
        <CardContent className="space-y-10 px-8 py-10">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 text-lg font-semibold text-foreground">
                  {t("users.detail.sections.personalInfo")}
                </h2>
                <div className="space-y-4">
                  {member.profileImage && (
                    <div className="flex items-center gap-3">
                      <img
                        src={member.profileImage}
                        alt=""
                        className="h-16 w-16 rounded-full object-cover"
                      />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("users.detail.fields.name")}
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      {displayName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("users.page.columns.email")}
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      {member.email}
                    </p>
                  </div>
                  {member.firstName != null && member.firstName !== "" && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t("users.detail.fields.firstName")}
                      </p>
                      <p className="mt-1 text-sm text-foreground">
                        {member.firstName}
                      </p>
                    </div>
                  )}
                  {member.lastName != null && member.lastName !== "" && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t("users.detail.fields.lastName")}
                      </p>
                      <p className="mt-1 text-sm text-foreground">
                        {member.lastName}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 text-lg font-semibold text-foreground">
                  {t("users.detail.sections.accountInfo")}
                </h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("users.page.columns.role")}
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      {t(`users.tenant.role.${member.role}`)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("users.page.columns.status")}
                    </p>
                    <div className="mt-1">
                      <StatusBadge variant={statusVariant(member.status)}>
                        {t(`users.page.status.${member.status}`)}
                      </StatusBadge>
                    </div>
                  </div>
                  {member.createdAt && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t("users.detail.fields.createdAt")}
                      </p>
                      <p className="mt-1 text-sm text-foreground">
                        {formatDisplayDate(member.createdAt, { locale: i18n.language })}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap justify-between gap-3 border-t px-8 py-6">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setResetPasswordOpen(true)}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Key className="mr-2 h-4 w-4" />
              )}
              {t("users.detail.actions.resetPassword")}
            </Button>
            {!isOwner && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setDisableOpen(true)}
                  disabled={setStatusMutation.isPending}
                >
                  {setStatusMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : member.status === "disabled" ? (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  ) : (
                    <Ban className="mr-2 h-4 w-4" />
                  )}
                  {member.status === "disabled"
                    ? t("users.detail.actions.enableAccount")
                    : t("users.detail.actions.disableAccount")}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteOpen(true)}
                  disabled={deleteMemberMutation.isPending}
                >
                  {deleteMemberMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {t("users.detail.actions.deleteAccount")}
                </Button>
              </>
            )}
          </div>
        </CardFooter>
      </Card>

      <Dialog open={changeRoleOpen} onOpenChange={setChangeRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.tenant.changeRoleTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              {displayName} ({member.email})
            </p>
            <div className="grid gap-2">
              <Label htmlFor="change-role-select">
                {t("users.invite.role")}
              </Label>
              <Select
                value={changeRoleSelectedRole}
                onValueChange={(v) =>
                  setChangeRoleSelectedRole(v as TenantInviteRole)
                }
              >
                <SelectTrigger id="change-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {editableRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`users.tenant.role.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangeRoleOpen(false)}
              disabled={updateRoleMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() =>
                updateRoleMutation.mutate({
                  userId: member.userId,
                  role: changeRoleSelectedRole,
                })
              }
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending
                ? t("users.invite.submitting")
                : t("users.tenant.changeRoleSave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("users.detail.dialogs.resetPassword.title")}
            </DialogTitle>
            <DialogDescription>
              {t("users.detail.dialogs.resetPassword.description", {
                email: member.email,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordOpen(false)}
              disabled={resetPasswordMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() => resetPasswordMutation.mutate(member.email)}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("users.invite.submitting")}
                </>
              ) : (
                t("users.detail.dialogs.resetPassword.title")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title={
          member.status === "disabled"
            ? t("users.detail.dialogs.enable.title")
            : t("users.detail.dialogs.disable.title")
        }
        description={
          member.status === "disabled"
            ? t("users.detail.dialogs.enable.description", {
                email: member.email,
              })
            : t("users.detail.dialogs.disable.description", {
                email: member.email,
              })
        }
        confirmText={
          member.status === "disabled"
            ? t("users.detail.actions.enableAccount")
            : t("users.detail.actions.disableAccount")
        }
        onConfirm={() =>
          setStatusMutation.mutate({
            userId: member.userId,
            status: member.status === "disabled" ? "active" : "disabled",
          })
        }
        isLoading={setStatusMutation.isPending}
      />

      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("users.detail.dialogs.delete.title")}
        description={t("users.detail.dialogs.delete.description")}
        confirmText={t("users.detail.actions.deleteAccount")}
        variant="destructive"
        onConfirm={() => deleteMemberMutation.mutate({ userId: member.userId })}
        isLoading={deleteMemberMutation.isPending}
      />
    </div>
  );
}
